import { useState, useMemo } from "react";
import type { Customer, Workflow } from "~/types/api";
import type { ListDTO } from "../../list.types";

type FormData = {
  name: string;
  customerId: number | null;
  workflowId: number | null;
};

export const useListForm = (
  mode: "create" | "edit",
  list?: ListDTO,
  allWorkflows?: Workflow[],
  allCustomers?: Customer[]
) => {
  const [formData, setFormData] = useState<FormData>({
    name: mode === "edit" && list ? list.name : "",
    customerId: mode === "edit" && list ? list.customer.id : null,
    workflowId: mode === "edit" && list ? list.workflow.id : null,
  });

  const filteredWorkflows = useMemo(
    () =>
      allWorkflows?.filter(
        (workflow) => !formData.customerId || workflow.customerId === formData.customerId
      ) || [],
    [allWorkflows, formData.customerId]
  );

  const customerOptions = useMemo(
    () => [
      { value: "", label: "All Customers" },
      ...(allCustomers?.map((customer) => ({
        value: customer.id.toString(),
        label: customer.name,
      })) || []),
    ],
    [allCustomers]
  );

  const workflowOptions = useMemo(
    () =>
      filteredWorkflows
        ?.filter((w) => w.state !== "Completed")
        .map((workflow) => {
          return {
            value: workflow.id.toString(),
            label: `${workflow.name} (v. ${String(workflow.version).padStart(2, "0")}) ${workflow.state === "WorkInProgress" ? "(WIP)" : ""}`,
          };
        }) || [],
    [filteredWorkflows]
  );

  const isValid = formData.name && formData.workflowId;

  const hasChanges = useMemo(() => {
    if (mode === "create") return true;
    if (!list) return false;

    return formData.name !== list.name || formData.workflowId !== list.workflow.id;
  }, [mode, list, formData]);

  return {
    formData,
    setFormData,
    filteredWorkflows,
    customerOptions,
    workflowOptions,
    isValid,
    hasChanges,
  };
};
